/**
 * Shared helpers for batch gear flows (Receive gear, Send gear, Batch inspect).
 *
 * Everything that touches the register in bulk goes through the same
 * chunking, CSV, and grouping helpers so the three screens behave the same.
 */

export const BATCH_CHUNK_SIZE = 100;

export interface BatchProgress {
  processed: number;
  total: number;
}

export function chunkArray<T>(items: T[], size: number): T[][] {
  if (size <= 0) throw new Error('Chunk size must be greater than zero.');
  const chunks: T[][] = [];
  for (let start = 0; start < items.length; start += size) {
    chunks.push(items.slice(start, start + size));
  }
  return chunks;
}

/**
 * Run `handler` sequentially over `items` in chunks, reporting progress after
 * each chunk. A chunk that throws is passed to `onChunkError` (if provided)
 * and processing continues with the next chunk; the batch never stops on a
 * single failed request.
 */
export async function runInChunks<T, R>(
  items: T[],
  size: number,
  handler: (chunk: T[], chunkIndex: number) => Promise<R>,
  options: {
    onProgress?: (progress: BatchProgress) => void;
    onChunkError?: (error: unknown, chunk: T[], chunkIndex: number) => R | void;
  } = {}
): Promise<R[]> {
  const results: R[] = [];
  const chunks = chunkArray(items, size);
  let processed = 0;
  options.onProgress?.({ processed: 0, total: items.length });
  for (const [index, chunk] of chunks.entries()) {
    try {
      results.push(await handler(chunk, index));
    } catch (error) {
      if (!options.onChunkError) throw error;
      const fallback = options.onChunkError(error, chunk, index);
      if (fallback !== undefined) results.push(fallback);
    }
    processed = Math.min(processed + chunk.length, items.length);
    options.onProgress?.({ processed, total: items.length });
  }
  return results;
}

export function csvCell(value: string | number | boolean | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildCsv(
  headers: string[],
  rows: Array<Array<string | number | boolean | null | undefined>>
): string {
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
}

export function slugifyFilename(name: string, fallback: string): string {
  const slug = name
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  return slug || fallback;
}

export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function todayInputValue(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export interface CategoryGroup<T> {
  key: string;
  label: string;
  items: T[];
}

export type BatchSortKey = 'category' | 'serial' | 'status' | 'entered';

/**
 * Group lines by asset category (carabiners, descenders, ropes…). Lines whose
 * asset could not be matched land in a trailing "Unmatched" group.
 */
export function groupByCategory<T>(
  lines: T[],
  getCategory: (line: T) => { key: string; label: string } | null,
  unmatchedLabel = 'Unmatched'
): CategoryGroup<T>[] {
  const groups = new Map<string, CategoryGroup<T>>();
  for (const line of lines) {
    const category = getCategory(line) ?? { key: '__unmatched__', label: unmatchedLabel };
    const existing = groups.get(category.key);
    if (existing) {
      existing.items.push(line);
    } else {
      groups.set(category.key, { key: category.key, label: category.label, items: [line] });
    }
  }
  return [...groups.values()].sort((a, b) => {
    if (a.key === '__unmatched__') return 1;
    if (b.key === '__unmatched__') return -1;
    return a.label.localeCompare(b.label);
  });
}

export function sortLines<T>(
  lines: T[],
  sortKey: BatchSortKey,
  accessors: {
    category: (line: T) => string;
    serial: (line: T) => string;
    status: (line: T) => string;
  }
): T[] {
  if (sortKey === 'entered') return [...lines];
  const compare = (a: T, b: T) => {
    if (sortKey === 'category') {
      const byCategory = accessors.category(a).localeCompare(accessors.category(b));
      if (byCategory !== 0) return byCategory;
    } else if (sortKey === 'status') {
      const byStatus = accessors.status(a).localeCompare(accessors.status(b));
      if (byStatus !== 0) return byStatus;
    }
    return accessors.serial(a).localeCompare(accessors.serial(b), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };
  return [...lines].sort(compare);
}
